/**
 * แปลงช่องที่อยู่แบบพิมพ์เอง (ແຂວງ/ເມືອງ/ບ້ານ ของผัว/เมีย พ่อแม่ …) เป็นรายการเลือกที่ผูกกันเป็นทอด
 * (province-name → district-name → village-name, เก็บเป็น **ชื่อ** — x-parent ชี้ช่องแม่) และช่อง ບ້ານ ที่ไม่มี
 * เมือง/แขวงคู่ เป็นช่อง ຄົ້ນຫາບ້ານ (village-search: พิมพ์ค้นทั่วประเทศ)
 *
 * แม่แบบ AI สร้างช่องเหล่านี้เป็นข้อความ (ผู้ร้องพิมพ์ชื่อบ้านผิด/ไม่ตรงทะเบียน) — แอปเลือกเมืองได้ต่อเมื่อเลือกแขวง
 * ในการกรอกครั้งนั้นแล้ว จึงต้องเรียง แขวง → เมือง → บ้าน ติดกันในขั้นเดียวกัน
 */

export type AddressKind = "province" | "district" | "village";

const WIDGET_OF: Record<AddressKind, string> = {
  province: "province-name",
  district: "district-name",
  village: "village-name",
};

const KEY_RE = /^(.*?)_?(province|district|village)(?:_name)?(?:_(\d+))?$/;
const LABEL_WORD: [string, AddressKind][] = [
  ["ແຂວງ", "province"],
  ["ເມືອງ", "district"],
  ["ບ້ານ", "village"],
];

interface Candidate {
  key: string;
  label: string;
  widget: string;
  page?: string;
  parent?: string;
}

/**
 * ชนิดช่องที่ปุ่มแปลงจัดการได้ — ข้อความ หรือที่ตั้งเป็นรายการเลือกไว้แล้ว (ผู้ดูแลเปลี่ยนชนิดทีละช่องแต่ยังไม่ได้
 * ผูกช่องแม่/เรียงลำดับ — ปุ่มซ่อมให้)
 */
const CONVERTIBLE = new Set(["text", "province-name", "district-name", "village-name", "village-search"]);

export interface AddressGroup {
  /** ชื่อกลุ่มที่แสดงให้ผู้ดูแลยืนยัน เช่น "ຜົວ/ເມຍ" (ว่าง = ที่อยู่หลักของเอกสาร) */
  name: string;
  province: string;
  district: string;
  village?: string;
}

const PREFIX_LO: Record<string, string> = {
  spouse: "ຜົວ/ເມຍ",
  father: "ພໍ່",
  mother: "ແມ່",
  child: "ລູກ",
  sibling: "ອ້າຍເອື້ອຍນ້ອງ",
  relative: "ຍາດພີ່ນ້ອງ",
  spouse_father: "ພໍ່ຂອງຜົວ/ເມຍ",
  spouse_mother: "ແມ່ຂອງຜົວ/ເມຍ",
  applicant: "ຜູ້ຮ້ອງ",
  land_owner: "ເຈົ້າຂອງດິນ",
};

/** "spouse" → "ຜົວ/ເມຍ", "child_2" → "ລູກ 2", "" → "" (ที่อยู่หลัก) — ไม่รู้จักใช้ key ตามเดิม */
function prefixName(prefix: string, n?: string): string {
  const m = /^(.*?)(?:_(\d+))?$/.exec(prefix);
  const base = m?.[1] ?? prefix;
  const no = m?.[2] ?? n;
  const lo = PREFIX_LO[base] ?? base;
  return no ? `${lo} ${no}` : lo;
}

/** ช่องนี้เป็นที่อยู่ชั้นไหน + กลุ่มอะไร — ไม่ใช่ = null */
function classify(f: Candidate): { kind: AddressKind; group: string; name: string } | null {
  const m = KEY_RE.exec(f.key);
  const label = f.label.trim();
  // ບ້ານເລກທີ / ເຮືອນເລກທີ คือเลขบ้าน ไม่ใช่ชื่อบ้าน
  if (/ເລກທີ/.test(label)) return null;
  if (m) {
    const word = LABEL_WORD.find(([, k]) => k === m[2])?.[0] ?? "";
    // ป้ายมักเป็นแค่ "ແຂວງ" (แม่แบบ AI) — ชื่อชุดเอาจากคำนำหน้า key แทน
    const fromLabel = label.startsWith(word) ? label.slice(word.length).replace(/^ຢູ່/, "").trim() : "";
    const name = fromLabel || prefixName(m[1] ?? "", m[3]);
    return { kind: m[2] as AddressKind, group: `k:${m[1] ?? ""}:${m[3] ?? ""}`, name };
  }
  for (const [word, kind] of LABEL_WORD) {
    if (label.startsWith(word)) {
      const rest = label.slice(word.length).replace(/^ຢູ່/, "").trim();
      return { kind, group: `l:${rest}`, name: rest };
    }
  }
  return null;
}

/**
 * หาชุดที่อยู่ที่แปลงได้ — ช่องข้อความเท่านั้น (ช่องที่อยู่ปัจจุบัน address-* ของระบบไม่แตะ), ชุดต้องมีทั้งแขวงและเมือง
 * (บ้านอย่างเดียวเลือกจากรายการไม่ได้ เพราะไม่รู้ว่าเมืองไหน) · `skipped` = ช่องที่ดูเป็นที่อยู่แต่ไม่ครบชุด
 */
export function findAddressGroups(fields: Candidate[]): {
  groups: AddressGroup[];
  /** ช่อง ບ້ານ ที่ไม่มีเมือง/แขวงคู่ — แปลงเป็นช่อง ຄົ້ນຫາບ້ານ (village-search) ได้ */
  loneVillages: string[];
  skipped: string[];
} {
  const byGroup = new Map<string, { name: string; slots: Partial<Record<AddressKind, string>> }>();
  for (const f of fields) {
    if (!CONVERTIBLE.has(f.widget)) continue;
    const c = classify(f);
    if (!c) continue;
    const g = byGroup.get(c.group) ?? { name: c.name, slots: {} };
    if (!g.slots[c.kind]) g.slots[c.kind] = f.key;
    if (!g.name) g.name = c.name;
    byGroup.set(c.group, g);
  }
  const groups: AddressGroup[] = [];
  const loneVillages: string[] = [];
  const skipped: string[] = [];
  const byKey = new Map(fields.map((f, i) => [f.key, { f, i }]));
  for (const g of byGroup.values()) {
    const { province, district, village } = g.slots;
    if (province && district) {
      const group = { name: g.name, province, district, ...(village ? { village } : {}) };
      if (!groupDone(group, byKey)) groups.push(group);
    } else if (village && !province && !district) {
      if (byKey.get(village)?.f.widget !== "village-search") loneVillages.push(village);
    } else skipped.push(...[province, district, village].filter((k): k is string => !!k));
  }
  return { groups, loneVillages, skipped };
}

/** ชุดนี้ถูกต้องแล้ว (ชนิด + ช่องแม่ + เรียงติดกัน แขวง → เมือง → บ้าน) — ไม่ต้องเสนอซ้ำ */
function groupDone(g: AddressGroup, byKey: Map<string, { f: Candidate; i: number }>): boolean {
  const p = byKey.get(g.province);
  const d = byKey.get(g.district);
  const v = g.village ? byKey.get(g.village) : undefined;
  if (!p || !d || p.f.widget !== "province-name" || d.f.widget !== "district-name" || d.f.parent !== g.province) {
    return false;
  }
  if (d.i !== p.i + 1) return false;
  if (!g.village) return true;
  return !!v && v.f.widget === "village-name" && v.f.parent === g.district && v.i === d.i + 1;
}

/**
 * ช่องแม่ที่ควรเป็นเมื่อผู้ดูแลเปลี่ยนช่องเป็น ເລືອກເມືອງ/ເລືອກບ້ານ เอง — ช่องแขวง/เมืองของคนเดียวกัน (คำนำหน้า key
 * หรือป้ายเดียวกัน) ก่อน ไม่มีก็ช่องที่ชนิดถูกที่อยู่ใกล้ที่สุดก่อนหน้า · หาไม่ได้ = ""
 */
export function guessParent(field: Candidate, fields: Candidate[]): string {
  const want = field.widget === "district-name" ? "province" : field.widget === "village-name" ? "district" : null;
  if (!want) return "";
  const wantWidget = want === "province" ? "province-name" : "district-name";
  const mine = classify(field);
  if (mine) {
    const same = fields.find((f) => f.key !== field.key && classify(f)?.group === mine.group && classify(f)?.kind === want);
    if (same) return same.key;
  }
  const at = fields.findIndex((f) => f.key === field.key);
  for (let i = at - 1; i >= 0; i--) {
    if (fields[i]?.widget === wantWidget) return fields[i]!.key;
  }
  return "";
}

/**
 * ใช้ผลกับรายการช่อง: ตั้งชนิด + ช่องแม่ แล้วย้ายทั้งชุดมาเรียง แขวง → เมือง → บ้าน ตรงตำแหน่งช่องแรกของชุด
 * (ขั้นตอนการกรอกตามช่องแรก) — คืนรายการใหม่ ไม่แก้ของเดิม
 */
export function applyAddressGroups<F extends Candidate & { parent: string }>(
  fields: F[],
  groups: AddressGroup[],
): F[] {
  let out = [...fields];
  for (const g of groups) {
    const keys = [g.province, g.district, ...(g.village ? [g.village] : [])];
    const at = Math.min(...keys.map((k) => out.findIndex((f) => f.key === k)));
    // ทั้งชุดอยู่ขั้นเดียวกับช่องแรก (ขั้นตอนการกรอก x-page / ขั้นตายตัว x-step ของ A01)
    const anchor = out[at] as (F & { step?: unknown }) | undefined;
    const page = anchor?.page;
    const step = anchor?.step;
    const parentOf: Record<string, string> = { [g.province]: "", [g.district]: g.province };
    if (g.village) parentOf[g.village] = g.district;
    const kindOf: Record<string, AddressKind> = { [g.province]: "province", [g.district]: "district" };
    if (g.village) kindOf[g.village] = "village";
    const moved = keys.map((k) => {
      const f = out.find((x) => x.key === k) as F;
      return {
        ...f,
        widget: WIDGET_OF[kindOf[k] as AddressKind],
        parent: parentOf[k] ?? "",
        ...(page !== undefined ? { page } : {}),
        ...(step !== undefined ? { step } : {}),
      };
    });
    const rest = out.filter((f) => !keys.includes(f.key));
    const insertAt = out.slice(0, at).filter((f) => !keys.includes(f.key)).length;
    out = [...rest.slice(0, insertAt), ...moved, ...rest.slice(insertAt)];
  }
  return out;
}
